import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiGet, apiSend, onUnauthorized, setCsrfToken } from '../lib/api.js';

export type SessionUser = {
  id: number;
  email: string;
  name: string;
  role: 'admin' | 'viewer';
  mustChangePassword: boolean;
};

export type AuthState = {
  status: 'loading' | 'anonymous' | 'authenticated';
  user: SessionUser | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
};

export const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthState['status']>('loading');
  const [user, setUser] = useState<SessionUser | null>(null);

  useEffect(() => {
    onUnauthorized(() => {
      setUser(null);
      setCsrfToken(null);
      setStatus('anonymous');
    });

    let cancelled = false;
    /* The token comes back with the user, and must be restored before anything
       can be saved: it lives in memory only, so a reload starts without one and
       every mutation would fail CSRF verification while the cookie stays
       perfectly valid. */
    void apiGet<{ user: SessionUser; csrfToken: string | null }>('/api/session')
      .then((data) => {
        if (cancelled) return;
        setCsrfToken(data.csrfToken);
        setUser(data.user);
        setStatus('authenticated');
      })
      .catch(() => {
        if (cancelled) return;
        setStatus('anonymous');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const data = await apiSend<{ user: SessionUser; csrfToken: string }>('POST', '/api/session', {
      email,
      password,
    });
    setCsrfToken(data.csrfToken);
    setUser(data.user);
    setStatus('authenticated');
  }, []);

  const signOut = useCallback(async () => {
    await apiSend('DELETE', '/api/session').catch(() => undefined);
    setCsrfToken(null);
    setUser(null);
    setStatus('anonymous');
  }, []);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    const data = await apiSend<{ csrfToken: string }>('POST', '/api/session/password', {
      currentPassword,
      newPassword,
    });
    // The server revoked every session and issued a new one.
    setCsrfToken(data.csrfToken);
    setUser((previous) => (previous === null ? null : { ...previous, mustChangePassword: false }));
  }, []);

  const value = useMemo<AuthState>(
    () => ({ status, user, signIn, signOut, changePassword }),
    [status, user, signIn, signOut, changePassword],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
